import { createRoot } from "react-dom/client";
import { Capacitor } from "@capacitor/core";
import App from "./App.tsx";
import "./index.css";
import { initSentry } from "./lib/sentry";

// Initialize Sentry as early as possible (before React renders)
initSentry();

function safeStorageGet(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

function safeStorageSet(storage: Storage, key: string, value: string): void {
  try {
    storage.setItem(key, value);
  } catch {
    // Non-fatal: storage can fail in restricted or low-quota environments.
  }
}

/**
 * Native (Android/iOS): on first launch after an app update, unregister any lingering
 * PWA service worker and purge all caches. This prevents a stale cached bundle —
 * which may reference an old localhost origin — from being served inside the WebView.
 * Runs only once per install/update cycle (tracked via localStorage).
 * If cleanup fails, we still mark it attempted to avoid repeated retry loops.
 */
async function clearNativeWebViewCache() {
  if (!Capacitor.isNativePlatform()) return false;
  if (Capacitor.getPlatform() === "ios") {
    console.log("[Buukd] iOS native platform, skipping service worker cache cleanup");
    return false;
  }
  if (!("serviceWorker" in navigator)) return false;

  const successKey = "__buukd_native_cache_cleared__";
  const attemptKey = "__buukd_native_cache_clear_attempted__";
  if (safeStorageGet(localStorage, successKey) === "1") return false;
  if (safeStorageGet(localStorage, attemptKey) === "1") return false;

  try {
    console.log("[Buukd] Starting native service worker/cache cleanup...");
    const regs = await navigator.serviceWorker.getRegistrations();
    await Promise.all(regs.map((r) => r.unregister()));

    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((k) => caches.delete(k)));
    }

    safeStorageSet(localStorage, successKey, "1");
    // WKWebView on iOS can stay blank after a hard reload; clear caches in-place instead.
    if (Capacitor.getPlatform() !== "ios") {
      window.location.reload();
      return true;
    }
    return false;
  } catch (error) {
    console.warn("[Buukd] Non-fatal: clearNativeWebViewCache failed:", error);
    return false;
  } finally {
    safeStorageSet(localStorage, attemptKey, "1");
  }
}

/**
 * Development only: if a PWA service worker from a previous production build is still
 * controlling localhost it can cause Supabase fetch errors ("Response body is locked").
 * Unregister it once and reload so the dev server takes over cleanly.
 */
async function clearStaleDevServiceWorker() {
  if (!import.meta.env.DEV) return false;
  if (!("serviceWorker" in navigator)) return false;
  const successKey = "__buukd_dev_sw_cleared__";
  const attemptKey = "__buukd_dev_sw_clear_attempted__";
  if (safeStorageGet(sessionStorage, successKey) === "1") return false;
  if (safeStorageGet(sessionStorage, attemptKey) === "1") return false;

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    if (!regs.length) return false;

    await Promise.all(regs.map((r) => r.unregister()));

    safeStorageSet(sessionStorage, successKey, "1");
    window.location.reload();
    return true;
  } catch {
    // Non-fatal.
    return false;
  } finally {
    safeStorageSet(sessionStorage, attemptKey, "1");
  }
}

// Register the PWA service worker for web production builds only.
// Native builds use bundled assets and must not cache via a service worker;
// development builds use the Vite dev server and must not interfere with hot-reload.
if ("serviceWorker" in navigator && import.meta.env.PROD && !Capacitor.isNativePlatform()) {
  import("virtual:pwa-register").then(({ registerSW }) => {
    const reloadSW = registerSW({
      onNeedRefresh() {
        (window as unknown as { __BUUKD_SW_NEED_REFRESH__?: boolean }).__BUUKD_SW_NEED_REFRESH__ = true;
        window.dispatchEvent(new CustomEvent("pwa-need-refresh"));
      },
      onOfflineReady() {
        console.log("App ready to work offline");
      },
      onRegistered(registration) {
        (window as unknown as { __BUUKD_SW_REGISTRATION__?: ServiceWorkerRegistration }).__BUUKD_SW_REGISTRATION__ =
          registration ?? undefined;
      },
    });
    (window as unknown as { __BUUKD_SW_RELOAD__?: (reloadPage?: boolean) => Promise<void> }).__BUUKD_SW_RELOAD__ = reloadSW;
  });
}

async function configureNativeStatusBar() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    const platform = Capacitor.getPlatform();

    if (platform === "android") {
      // Push web content below the status bar on Android.
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: "#ffffff" });
    }

    // This is supported on both Android and iOS.
    await StatusBar.setStyle({ style: Style.Dark });
  } catch {
    // Ignore if plugin is unavailable.
  }
}

async function configureNativeNotificationChannel() {
  if (!Capacitor.isNativePlatform()) return;

  try {
    const { LocalNotifications } = await import("@capacitor/local-notifications");

    // Ask for permission up-front so the dialog appears on first launch.
    const perms = await LocalNotifications.checkPermissions();
    if (perms.display !== "granted") {
      await LocalNotifications.requestPermissions();
    }

    // Create the notification channel used by sendNativePush in use-notifications.tsx.
    // createChannel is idempotent on Android — safe to call on every launch.
    await LocalNotifications.createChannel({
      id: "buukd-general",
      name: "Buukd Alerts",
      description: "Booking and business alerts",
      importance: 5,
      visibility: 1,
      sound: "default",
      vibration: true,
    });
  } catch {
    // Ignore errors in web context or if the user denies permission.
  }
}

async function bootstrapApp() {
  try {
    console.log("[Buukd] Starting bootstrapApp...");
    const nativeReloadTriggered = await clearNativeWebViewCache();
    console.log("[Buukd] clearNativeWebViewCache finished, nativeReloadTriggered:", nativeReloadTriggered);
    if (nativeReloadTriggered) return;

    const devReloadTriggered = await clearStaleDevServiceWorker();
    console.log("[Buukd] clearStaleDevServiceWorker finished, devReloadTriggered:", devReloadTriggered);
    if (devReloadTriggered) return;

    void configureNativeStatusBar();
    void configureNativeNotificationChannel();

    console.log("[Buukd] Rendering React root element...");
    const rootEl = document.getElementById("root");
    if (!rootEl) {
      throw new Error("Element with id 'root' was not found in the document");
    }
    createRoot(rootEl).render(<App />);
    console.log("[Buukd] bootstrapApp rendering complete");
  } catch (error) {
    console.error("[Buukd] CRITICAL BOOTSTRAP ERROR:", error);
  }
}

void bootstrapApp();
